import { BucketVencimientoBadge, FechaCR, MontoCRC } from '@/components/dominio';
import { MultiSelect } from '@/components/filtros/MultiSelect';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useSincronizarPagos } from '@/hooks/useSincronizarPagos';
import { useVencimientosDetalle } from '@/hooks/useVencimientosDetalle';
import { cn } from '@/lib/utils';
import type {
  BucketVencimiento,
  VencimientoDetalle,
  VencimientoPorProveedor,
} from '@adelante/shared';
import { formatearCRC } from '@adelante/shared';
import { AllCommunityModule, type ColDef, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import {
  ArrowSquareOut,
  ArrowsClockwise,
  CaretDown,
  CaretUp,
  CircleNotch,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import ReactECharts from 'echarts-for-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

ModuleRegistry.registerModules([AllCommunityModule]);

const ORDEN_BUCKETS: BucketVencimiento[] = [
  'Vencido',
  'Esta semana',
  'Próxima semana',
  '2-4 semanas',
  '30-60 días',
  '60+ días',
];

const OPCIONES_BUCKET = ORDEN_BUCKETS.map((b) => ({ value: b, label: b }));

/** Formato compacto para el eje del gráfico (₡2.7M en vez de ₡2,698,214.00). */
function formatearCompacto(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `₡${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `₡${(v / 1_000).toFixed(0)}k`;
  return formatearCRC(v);
}

type CampoOrdenFactura = 'externalDocumentNo' | 'dueDate' | 'bucketVencimiento' | 'remainingAmtLCY';

export function VencimientosPantalla() {
  const { data, isLoading, error } = useVencimientosDetalle();
  const sincronizar = useSincronizarPagos();

  const [busqueda, setBusqueda] = useState('');
  const [terminosFiltro, setTerminosFiltro] = useState<string[]>([]);
  const [bucketsFiltro, setBucketsFiltro] = useState<string[]>([]);
  const [monedaFiltro, setMonedaFiltro] = useState<string[]>([]);
  const [proveedoresFiltro, setProveedoresFiltro] = useState<string[]>([]);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [proveedorAbierto, setProveedorAbierto] = useState<string | null>(null);
  const [ordenFactura, setOrdenFactura] = useState<{
    campo: CampoOrdenFactura;
    dir: 'asc' | 'desc';
  }>({ campo: 'dueDate', dir: 'asc' });

  const detalle = data?.data ?? [];

  const opcionesTerminos = useMemo(() => {
    const set = new Set<string>();
    for (const f of detalle) {
      if (f.paymentTermsCode) set.add(f.paymentTermsCode);
    }
    return [...set].sort().map((t) => ({ value: t, label: t }));
  }, [detalle]);

  const opcionesMoneda = useMemo(() => {
    const set = new Set<string>();
    for (const f of detalle) set.add(f.currencyCode || 'CRC');
    return [...set].sort().map((m) => ({ value: m, label: m }));
  }, [detalle]);

  const opcionesProveedores = useMemo(() => {
    const porCodigo = new Map<string, string>();
    for (const f of detalle) {
      if (!porCodigo.has(f.vendorCode)) porCodigo.set(f.vendorCode, f.vendorName ?? f.vendorCode);
    }
    return [...porCodigo.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, nombre]) => ({ value, label: nombre }));
  }, [detalle]);

  const detalleFiltrado = useMemo(() => {
    const busquedaNorm = busqueda.trim().toLowerCase();
    return detalle.filter((f) => {
      if (terminosFiltro.length > 0 && !terminosFiltro.includes(f.paymentTermsCode ?? '')) {
        return false;
      }
      if (bucketsFiltro.length > 0 && !bucketsFiltro.includes(f.bucketVencimiento)) {
        return false;
      }
      if (monedaFiltro.length > 0 && !monedaFiltro.includes(f.currencyCode || 'CRC')) {
        return false;
      }
      if (proveedoresFiltro.length > 0 && !proveedoresFiltro.includes(f.vendorCode)) {
        return false;
      }
      if (fechaDesde && (!f.dueDate || f.dueDate < fechaDesde)) return false;
      if (fechaHasta && (!f.dueDate || f.dueDate > fechaHasta)) return false;
      if (busquedaNorm) {
        const coincide =
          f.vendorName?.toLowerCase().includes(busquedaNorm) ||
          f.vendorCode.toLowerCase().includes(busquedaNorm);
        if (!coincide) return false;
      }
      return true;
    });
  }, [
    detalle,
    terminosFiltro,
    bucketsFiltro,
    monedaFiltro,
    proveedoresFiltro,
    fechaDesde,
    fechaHasta,
    busqueda,
  ]);

  const totalPendiente = detalleFiltrado.reduce((acc, f) => acc + (f.remainingAmtLCY ?? 0), 0);
  const totalVencido = detalleFiltrado
    .filter((f) => f.bucketVencimiento === 'Vencido')
    .reduce((acc, f) => acc + (f.remainingAmtLCY ?? 0), 0);
  const cantidadFacturas = detalleFiltrado.length;

  const filasPorProveedor = useMemo<VencimientoPorProveedor[]>(() => {
    const porVendor = new Map<string, VencimientoDetalle[]>();
    for (const f of detalleFiltrado) {
      const lista = porVendor.get(f.vendorCode) ?? [];
      lista.push(f);
      porVendor.set(f.vendorCode, lista);
    }
    return [...porVendor.entries()]
      .map(([vendorCode, facturas]) => {
        const monedas = new Set(facturas.map((f) => f.currencyCode || 'CRC'));
        const fechas = facturas.map((f) => f.dueDate).filter((d): d is string => !!d);
        return {
          vendorCode,
          vendorName: facturas[0]?.vendorName ?? null,
          paymentTermsCode: facturas[0]?.paymentTermsCode ?? null,
          paymentMethodCode: facturas[0]?.paymentMethodCode ?? null,
          currencyCode: monedas.size === 1 ? ([...monedas][0] ?? null) : 'Mixto',
          proximoVencimiento: fechas.length > 0 ? (fechas.sort()[0] ?? null) : null,
          totalPendienteLCY: facturas.reduce((acc, f) => acc + (f.remainingAmtLCY ?? 0), 0),
          cantidadFacturas: facturas.length,
        };
      })
      .sort((a, b) => (a.proximoVencimiento ?? '').localeCompare(b.proximoVencimiento ?? ''));
  }, [detalleFiltrado]);

  const facturasProveedorAbierto = useMemo(() => {
    if (!proveedorAbierto) return [];
    const { campo, dir } = ordenFactura;
    const signo = dir === 'asc' ? 1 : -1;
    return detalleFiltrado
      .filter((f) => f.vendorCode === proveedorAbierto)
      .sort((a, b) => {
        const va = a[campo];
        const vb = b[campo];
        if (va === vb) return 0;
        if (va === null) return 1;
        if (vb === null) return -1;
        return va < vb ? -signo : signo;
      });
  }, [detalleFiltrado, proveedorAbierto, ordenFactura]);

  function alternarOrden(campo: CampoOrdenFactura) {
    setOrdenFactura((actual) =>
      actual.campo === campo
        ? { campo, dir: actual.dir === 'asc' ? 'desc' : 'asc' }
        : { campo, dir: 'asc' },
    );
  }

  const opcionEChart = useMemo(() => {
    const monedas = [...new Set(detalleFiltrado.map((f) => f.currencyCode || 'CRC'))].sort();
    const series = monedas.map((moneda) => ({
      name: moneda,
      type: 'bar' as const,
      stack: 'total',
      emphasis: { focus: 'series' as const },
      data: ORDEN_BUCKETS.map((bucket) =>
        detalleFiltrado
          .filter((f) => f.bucketVencimiento === bucket && (f.currencyCode || 'CRC') === moneda)
          .reduce((acc, f) => acc + (f.remainingAmtLCY ?? 0), 0),
      ),
    }));

    return {
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: { type: 'shadow' as const },
        valueFormatter: (v: number) => formatearCRC(v),
      },
      legend: { data: monedas },
      grid: { left: 90, right: 20, top: 40, bottom: 30 },
      xAxis: { type: 'category' as const, data: ORDEN_BUCKETS },
      yAxis: {
        type: 'value' as const,
        axisLabel: { formatter: (v: number) => formatearCompacto(v) },
      },
      series,
    };
  }, [detalleFiltrado]);

  const columnas = useMemo<ColDef<VencimientoPorProveedor>[]>(
    () => [
      { field: 'vendorCode', headerName: 'Código', width: 110 },
      { field: 'vendorName', headerName: 'Proveedor', flex: 1, minWidth: 200 },
      { field: 'paymentTermsCode', headerName: 'Términos', width: 110 },
      { field: 'currencyCode', headerName: 'Moneda', width: 100 },
      {
        field: 'proximoVencimiento',
        headerName: 'Próximo vencimiento',
        width: 160,
        cellRenderer: (p: { value: string | null }) =>
          p.value ? <FechaCR fecha={p.value} /> : '—',
      },
      {
        field: 'totalPendienteLCY',
        headerName: 'Total pendiente',
        width: 170,
        type: 'rightAligned',
        cellRenderer: (p: { value: number }) => <MontoCRC monto={p.value} />,
      },
      { field: 'cantidadFacturas', headerName: 'Facturas', width: 100, type: 'rightAligned' },
    ],
    [],
  );

  const cargando = isLoading;

  function encabezadoOrdenable(etiqueta: string, campo: CampoOrdenFactura, alinearDerecha = false) {
    const activo = ordenFactura.campo === campo;
    return (
      <button
        type="button"
        onClick={() => alternarOrden(campo)}
        className={cn('flex items-center gap-1 hover:text-foreground', alinearDerecha && 'ml-auto')}
      >
        {etiqueta}
        {activo &&
          (ordenFactura.dir === 'asc' ? (
            <CaretUp className="h-3 w-3" weight="bold" aria-hidden="true" />
          ) : (
            <CaretDown className="h-3 w-3" weight="bold" aria-hidden="true" />
          ))}
      </button>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Vencimientos de proveedores</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Proyección de pagos pendientes por proveedor, moneda y forma de pago.
          </p>
        </div>
        <Button
          variant="outline"
          disabled={sincronizar.isPending}
          onClick={() =>
            toast.promise(sincronizar.mutateAsync(), {
              loading: 'Sincronizando con Business Central...',
              success: (r) =>
                `Sync completo: ${r.data?.entriesSincronizados ?? 0} facturas, ${
                  r.data?.vendorsSincronizados ?? 0
                } proveedores.${
                  r.data?.proveedoresCreados
                    ? ` Se crearon ${r.data.proveedoresCreados} proveedor(es) nuevo(s) en la app: falta agregarles la cuenta.`
                    : ''
                }`,
              error: (e) =>
                `Error al sincronizar: ${e instanceof Error ? e.message : 'desconocido'}`,
            })
          }
        >
          {sincronizar.isPending ? (
            <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
          ) : (
            <ArrowsClockwise className="h-4 w-4" weight="bold" aria-hidden="true" />
          )}
          Sincronizar con BC
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="relative">
          <MagnifyingGlass
            className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
            weight="bold"
            aria-hidden="true"
          />
          <Input
            placeholder="Buscar proveedor..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-[220px] pl-8"
          />
        </div>

        <MultiSelect
          name="terminos"
          placeholder="Términos de pago"
          opciones={opcionesTerminos}
          seleccionados={terminosFiltro}
          onChange={setTerminosFiltro}
          className="w-[180px] justify-between font-normal"
        />

        <MultiSelect
          name="buckets"
          placeholder="Bucket de vencimiento"
          opciones={OPCIONES_BUCKET}
          seleccionados={bucketsFiltro}
          onChange={setBucketsFiltro}
          className="w-[190px] justify-between font-normal"
        />

        <MultiSelect
          name="moneda"
          placeholder="Moneda"
          opciones={opcionesMoneda}
          seleccionados={monedaFiltro}
          onChange={setMonedaFiltro}
          className="w-[140px] justify-between font-normal"
        />

        <MultiSelect
          name="proveedores"
          placeholder="Todos los proveedores"
          opciones={opcionesProveedores}
          seleccionados={proveedoresFiltro}
          onChange={setProveedoresFiltro}
          todosPorDefecto
          conBusqueda
          className="w-[200px] justify-between font-normal"
        />

        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor="fecha-desde" className="text-xs text-muted-foreground">
              Vence desde
            </Label>
            <Input
              id="fecha-desde"
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              className="w-[150px]"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="fecha-hasta" className="text-xs text-muted-foreground">
              Vence hasta
            </Label>
            <Input
              id="fecha-hasta"
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              className="w-[150px]"
            />
          </div>
          {(fechaDesde || fechaHasta) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFechaDesde('');
                setFechaHasta('');
              }}
            >
              Limpiar fechas
            </Button>
          )}
        </div>
      </div>

      {cargando ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
          Cargando…
        </div>
      ) : error ? (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Error al cargar: {error.message}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Card className="border-l-4 border-l-adelante-primary">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                  Total pendiente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MontoCRC monto={totalPendiente} className="text-3xl font-semibold" />
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-destructive">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                  Vencido
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MontoCRC monto={totalVencido} className="text-3xl font-semibold" />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                  Facturas pendientes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-semibold tabular-nums">{cantidadFacturas}</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Por bucket de vencimiento</CardTitle>
            </CardHeader>
            <CardContent>
              <ReactECharts option={opcionEChart} style={{ height: 320 }} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Detalle por proveedor</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-2 text-xs text-muted-foreground">
                Click en un proveedor para ver sus facturas.
              </p>
              <div className="ag-theme-quartz" style={{ height: 480, width: '100%' }}>
                <AgGridReact
                  theme="legacy"
                  rowData={filasPorProveedor}
                  columnDefs={columnas}
                  defaultColDef={{ sortable: true, resizable: true }}
                  onRowClicked={(e) => e.data && setProveedorAbierto(e.data.vendorCode)}
                  rowStyle={{ cursor: 'pointer' }}
                />
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <Dialog
        open={proveedorAbierto !== null}
        onOpenChange={(open) => !open && setProveedorAbierto(null)}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {facturasProveedorAbierto[0]?.vendorName ?? proveedorAbierto} — Facturas pendientes
            </DialogTitle>
            <DialogDescription className="sr-only">
              Lista de facturas pendientes del proveedor seleccionado.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap gap-1.5">
            {ORDEN_BUCKETS.map((bucket) => {
              const activo = bucketsFiltro.includes(bucket);
              return (
                <button
                  key={bucket}
                  type="button"
                  onClick={() =>
                    setBucketsFiltro((actual) =>
                      actual.includes(bucket)
                        ? actual.filter((b) => b !== bucket)
                        : [...actual, bucket],
                    )
                  }
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs transition-colors',
                    activo
                      ? 'border-adelante-primary bg-adelante-primary/20 text-adelante-primary-dark'
                      : 'border-input text-muted-foreground hover:bg-accent',
                  )}
                >
                  {bucket}
                </button>
              );
            })}
            {bucketsFiltro.length > 0 && (
              <button
                type="button"
                onClick={() => setBucketsFiltro([])}
                className="text-xs text-muted-foreground underline"
              >
                Limpiar
              </button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Documento</TableHead>
                  <TableHead>{encabezadoOrdenable('# Factura', 'externalDocumentNo')}</TableHead>
                  <TableHead>{encabezadoOrdenable('Vencimiento', 'dueDate')}</TableHead>
                  <TableHead>{encabezadoOrdenable('Bucket', 'bucketVencimiento')}</TableHead>
                  <TableHead className="text-right">
                    {encabezadoOrdenable('Monto pendiente', 'remainingAmtLCY', true)}
                  </TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {facturasProveedorAbierto.map((f) => (
                  <TableRow key={f.entryNo}>
                    <TableCell className="font-mono text-xs">{f.documentNo ?? '—'}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {f.externalDocumentNo ?? '—'}
                    </TableCell>
                    <TableCell>{f.dueDate ? <FechaCR fecha={f.dueDate} /> : '—'}</TableCell>
                    <TableCell>
                      <BucketVencimientoBadge bucket={f.bucketVencimiento} />
                    </TableCell>
                    <TableCell className="text-right">
                      <MontoCRC monto={f.remainingAmtLCY ?? 0} />
                    </TableCell>
                    <TableCell>
                      {f.linkBC && (
                        <a
                          href={f.linkBC}
                          target="_blank"
                          rel="noreferrer"
                          title="Ver en Business Central"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <ArrowSquareOut className="h-4 w-4" weight="bold" aria-hidden="true" />
                        </a>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
